import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["sdkKey", "updateUserCompletionCallback", "perimeter"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import Statsig, { DynamicConfig, EvaluationReason, Layer } from 'statsig-js-lite';
import Fetcher from './fetcher';
import { FeatureGateEnvironment, PerimeterType } from './types';
import { CLIENT_VERSION } from './version';
export { DynamicConfig, EvaluationReason } from 'statsig-js-lite';
export { FeatureGateEnvironment, PerimeterType } from './types';
var DEFAULT_CLIENT_KEY = 'client-default-key';
// the default event logging api is the Atlassian proxy rather than Statsig's domain, to avoid ad blockers
var DEFAULT_EVENT_LOGGING_API = 'https://xp.atlassian.com/v1/';

/**
 * Access the FeatureGates object via the default export.
 * ```ts
 * import FeatureGates from '@atlaskit/feature-gate-js-client';
 * ```
 */
var FeatureGates = /*#__PURE__*/function () {
  function FeatureGates() {
    _classCallCheck(this, FeatureGates);
  }
  _createClass(FeatureGates, null, [{
    key: "initialize",
    value: (
    /**
     * @description
     * This method initializes the client using a network call to fetch the bootstrap values.
     * If the client is inialized with an `analyticsWebClient`, it will send an operational event
     * to GASv3 with the following attributes:
     * - targetApp: the target app of the client
     * - clientVersion: the version of the client
     * - success: whether the initialization was successful
     * - startTime: the time when the initialization started
     * - totalTime: the total time it took to initialize the client
     * - apiKey: the api key used to initialize the client
     * @param clientOptions {ClientOptions}
     * @param identifiers {Identifiers}
     * @param customAttributes {CustomAttributes}
     * @returns {Promise<void>}
     */
    function () {
      var _initialize = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(clientOptions, identifiers, customAttributes) {
        var startTime;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (!FeatureGates.initPromise) {
                _context.next = 3;
                break;
              }
              if (!FeatureGates.shallowEquals(clientOptions, FeatureGates.initOptions)) {
                // eslint-disable-next-line no-console
                console.warn('Feature Gates client already initialized with different options. New options were not applied.');
              }
              return _context.abrupt("return", FeatureGates.initPromise);
            case 3:
              startTime = performance.now();
              FeatureGates.initOptions = clientOptions;
              FeatureGates.initPromise = FeatureGates.init(clientOptions, identifiers, customAttributes).then(function () {
                FeatureGates.initCompleted = true;
              }).finally(function () {
                var endTime = performance.now();
                var totalTime = endTime - startTime;
                FeatureGates.fireClientEvent(startTime, totalTime, 'initialize', FeatureGates.initCompleted, clientOptions.apiKey);
              });
              return _context.abrupt("return", FeatureGates.initPromise);
            case 7:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      function initialize(_x, _x2, _x3) {
        return _initialize.apply(this, arguments);
      }
      return initialize;
    }())
  }, {
    key: "fireClientEvent",
    value: function fireClientEvent(startTime, totalTime, action, success) {
      var _FeatureGates$initOpt;
      var apiKey = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : undefined;
      (_FeatureGates$initOpt = FeatureGates.initOptions.analyticsWebClient) === null || _FeatureGates$initOpt === void 0 || _FeatureGates$initOpt.then(function (analyticsWebClient) {
        var attributes = _objectSpread({
          targetApp: FeatureGates.initOptions.targetApp,
          clientVersion: CLIENT_VERSION,
          success: success,
          startTime: startTime,
          totalTime: totalTime
        }, apiKey && {
          apiKey: apiKey
        });
        analyticsWebClient.sendOperationalEvent({
          action: action,
          actionSubject: 'featureGatesClient',
          attributes: attributes,
          tags: ['measurement'],
          source: '@atlaskit/feature-gate-js-client'
        });
      }).catch(function (err) {
        if (FeatureGates.initOptions.environment !== FeatureGateEnvironment.Production) {
          // eslint-disable-next-line no-console
          console.error('Analytics web client promise did not resolve', err);
        }
      });
    }
  }, {
    key: "initializeFromValues",
    value: function () {
      var _initializeFromValues = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(clientOptions, identifiers, customAttributes) {
        var initializeValues,
          startTime,
          _args2 = arguments;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              initializeValues = _args2.length > 3 && _args2[3] !== undefined ? _args2[3] : {};
              if (!FeatureGates.initPromise) {
                _context2.next = 4;
                break;
              }
              if (!FeatureGates.shallowEquals(clientOptions, FeatureGates.initOptions)) {
                // eslint-disable-next-line no-console
                console.warn('Feature Gates client already initialized with different options. New options were not applied.');
              }
              return _context2.abrupt("return", FeatureGates.initPromise);
            case 4:
              startTime = performance.now();
              FeatureGates.initOptions = clientOptions;
              FeatureGates.initPromise = FeatureGates.initFromValues(clientOptions, identifiers, customAttributes, initializeValues).then(function () {
                FeatureGates.initCompleted = true;
              }).finally(function () {
                var endTime = performance.now();
                var totalTime = endTime - startTime;
                FeatureGates.fireClientEvent(startTime, totalTime, 'initializeFromValues', FeatureGates.initCompleted);
              });
              return _context2.abrupt("return", FeatureGates.initPromise);
            case 8:
            case "end":
              return _context2.stop();
          }
        }, _callee2);
      }));
      function initializeFromValues(_x4, _x5, _x6) {
        return _initializeFromValues.apply(this, arguments);
      }
      return initializeFromValues;
    }()
    /**
     * This method updates the user using a network call to fetch the new set of values.
     * @param fetchOptions {FetcherOptions}
     * @param identifiers {Identifiers}
     * @param customAttributes {CustomAttributes}
     */
  }, {
    key: "updateUser",
    value: (function () {
      var _updateUser = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(fetchOptions, identifiers, customAttributes) {
        var initializeValuesProducer;
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              initializeValuesProducer = function initializeValuesProducer() {
                return Fetcher.fetchExperimentValues(fetchOptions, identifiers, customAttributes).then(function (_ref) {
                  var experimentValues = _ref.experimentValues,
                    customAttributes = _ref.customAttributes;
                  return {
                    experimentValues: experimentValues,
                    customAttributesFromFetch: customAttributes
                  };
                });
              };
              _context3.next = 3;
              return FeatureGates.updateUserUsingInitializeValuesProducer(initializeValuesProducer, identifiers, customAttributes);
            case 3:
            case "end":
              return _context3.stop();
          }
        }, _callee3);
      }));
      function updateUser(_x7, _x8, _x9) {
        return _updateUser.apply(this, arguments);
      }
      return updateUser;
    }()
    /**
     * This method updates the user given a new set of bootstrap values obtained from one of the server-side SDKs.
     *
     * @param identifiers {Identifiers}
     * @param customAttributes {CustomAttributes}
     * @param initializeValues {Record<string,unknown>}
     */
    )
  }, {
    key: "updateUserWithValues",
    value: (function () {
      var _updateUserWithValues = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(identifiers, customAttributes) {
        var initializeValues,
          initializeValuesProducer,
          _args4 = arguments;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              initializeValues = _args4.length > 2 && _args4[2] !== undefined ? _args4[2] : {};
              initializeValuesProducer = function initializeValuesProducer() {
                return Promise.resolve({
                  experimentValues: initializeValues,
                  customAttributesFromFetch: customAttributes
                });
              };
              _context4.next = 4;
              return FeatureGates.updateUserUsingInitializeValuesProducer(initializeValuesProducer, identifiers, customAttributes);
            case 4:
            case "end":
              return _context4.stop();
          }
        }, _callee4);
      }));
      function updateUserWithValues(_x10, _x11) {
        return _updateUserWithValues.apply(this, arguments);
      }
      return updateUserWithValues;
    }())
  }, {
    key: "initializeCalled",
    value: function initializeCalled() {
      return FeatureGates.initPromise != null;
    }
  }, {
    key: "initializeCompleted",
    value: function initializeCompleted() {
      return FeatureGates.initCompleted;
    }

    /**
     * Returns the value for a feature gate. Returns false if there are errors.
     * @param {string} gateName - The name of the feature gate.
     * @param {Object} options
     * @param {boolean} options.fireGateExposure
     *        Whether or not to fire the exposure event for the gate. Defaults to true.
     *        To log an exposure event manually at a later time, use {@link FeatureGates.manuallyLogGateExposure}
     *        (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-)).
     */
  }, {
    key: "checkGate",
    value: function checkGate(gateName) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      try {
        var _options$fireGateExpo = options.fireGateExposure,
          fireGateExposure = _options$fireGateExpo === void 0 ? true : _options$fireGateExpo;
        var evalMethod = fireGateExposure ? Statsig.checkGate.bind(Statsig) : Statsig.checkGateWithExposureLoggingDisabled.bind(Statsig);
        return evalMethod(gateName);
      } catch (error) {
        // Log the first occurrence of the error
        if (!FeatureGates.hasCheckGateErrorOccurred) {
          // eslint-disable-next-line no-console
          console.warn({
            msg: 'An error has occurred checking the feature gate. Only the first occurrence of this error is logged.',
            gateName: gateName,
            error: error
          });
          FeatureGates.hasCheckGateErrorOccurred = true;
        }
        return false;
      }
    }

    /**
     * Manually log a gate exposure (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-)).
     * This is useful if you have evaluated a gate earlier via {@link FeatureGates.checkGate} where <code>options.fireGateExposure</code> is false.
     * @param gateName
     */
  }, {
    key: "manuallyLogGateExposure",
    value: function manuallyLogGateExposure(gateName) {
      Statsig.manuallyLogGateExposure(gateName);
    }

    /**
     * Returns the entire config for a given experiment.
     *
     * @param {string} experimentName - The name of the experiment
     * @param {Object} options
     * @param {boolean} options.fireExperimentExposure - Whether or not to fire the exposure event for the experiment. Defaults to true. To log an exposure event manually at a later time, use {@link FeatureGates.manuallyLogExperimentExposure} (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-)).
     * @returns The config for an experiment
     * @example
     * ```ts
     * const experimentConfig = FeatureGates.getExperiment('example-experiment-name');
     * const backgroundColor: string = experimentConfig.get('backgroundColor', 'yellow');
     * ```
     */
  }, {
    key: "getExperiment",
    value: function getExperiment(experimentName) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      try {
        var _options$fireExperime = options.fireExperimentExposure,
          fireExperimentExposure = _options$fireExperime === void 0 ? true : _options$fireExperime;
        var evalMethod = fireExperimentExposure ? Statsig.getExperiment.bind(Statsig) : Statsig.getExperimentWithExposureLoggingDisabled.bind(Statsig);
        return evalMethod(experimentName);
      } catch (error) {
        // Log the first occurrence of the error
        if (!FeatureGates.hasGetExperimentErrorOccurred) {
          // eslint-disable-next-line no-console
          console.warn({
            msg: 'An error has occurred getting the experiment. Only the first occurrence of this error is logged.',
            experimentName: experimentName,
            error: error
          });
          FeatureGates.hasGetExperimentErrorOccurred = true;
        }

        // Return a default value
        return new DynamicConfig(experimentName, {}, '', {
          time: Date.now(),
          reason: EvaluationReason.Error
        });
      }
    }

    /**
      * Returns the value of a given parameter in an experiment config.
      *
      * @template T
      * @param {string} experimentName - The name of the experiment
      * @param {string} parameterName - The name of the parameter to fetch from the experiment config
      * @param {T} defaultValue - The value to serve if the experiment or parameter do not exist, or if the returned value does not match the expected type.
      * @param {Object} options
      * @param {boolean} options.fireExperimentExposure - Whether or not to fire the exposure event for the experiment. Defaults to true. To log an exposure event manually at a later time, use {@link FeatureGates.manuallyLogExperimentExposure} (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-))
      * @param {function} options.typeGuard - A function that asserts that the return value has the expected type. If this function returns false, then the default value will be returned instead. This can be set to protect your code from unexpected values being set remotely. By default, this will be done by asserting that the default value and value are the same primitive type.
      * @returns The value of the parameter if the experiment and parameter both exist, otherwise the default value.
      * @example
      ``` ts
      type ValidColor = 'blue' | 'red' | 'yellow';
      type ValidColorTypeCheck = (value: unknown) => value is ValidColor;
       const isValidColor: ValidColorTypeCheck =
         (value: unknown) => typeof value === 'string' && ['blue', 'red', 'yellow'].includes(value);
       const buttonColor: ValidColor = FeatureGates.getExperimentValue(
         'example-experiment-name',
         'backgroundColor',
         'yellow',
         {
             typeGuard: isValidColor
         }
      );
      ```
     */
  }, {
    key: "getExperimentValue",
    value: function getExperimentValue(experimentName, parameterName, defaultValue) {
      var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      var experiment = FeatureGates.getExperiment(experimentName, options);
      try {
        var typeGuard = options.typeGuard;
        return experiment.get(parameterName, defaultValue, typeGuard);
      } catch (error) {
        // Log the first occurrence of the error
        if (!FeatureGates.hasGetExperimentValueErrorOccurred) {
          // eslint-disable-next-line no-console
          console.warn({
            msg: 'An error has occurred getting the experiment value. Only the first occurrence of this error is logged.',
            experimentName: experimentName,
            defaultValue: defaultValue,
            options: options,
            error: error
          });
          FeatureGates.hasGetExperimentValueErrorOccurred = true;
        }
        return defaultValue;
      }
    }

    /**
     * Manually log an experiment exposure (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-)).
     * This is useful if you have evaluated an experiment earlier via {@link FeatureGates.getExperimentValue} or
     * {@link FeatureGates.getExperiment} where <code>options.fireExperimentExposure</code> is false.
     * @param experimentName
     */
  }, {
    key: "manuallyLogExperimentExposure",
    value: function manuallyLogExperimentExposure(experimentName) {
      Statsig.manuallyLogExperimentExposure(experimentName);
    }

    /**
     * Manually log a layer exposure (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-)).
     * This is useful if you have evaluated a layer earlier via {@link FeatureGates.getLayerValue} where <code>options.fireExperimentExposure</code> is false.
     * @param layerName
     * @param parameterName
     */
  }, {
    key: "manuallyLogLayerExposure",
    value: function manuallyLogLayerExposure(layerName, parameterName) {
      Statsig.manuallyLogLayerParameterExposure(layerName, parameterName);
    }
  }, {
    key: "shutdownStatsig",
    value: function shutdownStatsig() {
      Statsig.shutdown();
    }

    /**
     * Adds a new override for the given gate.
     *
     * This method is additive, meaning you can call it multiple times with different gate names to build
     * your full set of overrides.
     *
     * Overrides are persisted to the `STATSIG_JS_LITE_LOCAL_OVERRIDES` key in localStorage, so they will
     * continue to affect every client that is initialized on the same domain after this method is called.
     * If you are using this API for testing purposes, you should call ${@link FeatureGates.clearGateOverride} after
     * your tests are completed to remove this localStorage entry.
     *
     * @param {string} gateName
     * @param {boolean} value
     */
  }, {
    key: "overrideGate",
    value: function overrideGate(gateName, value) {
      Statsig.overrideGate(gateName, value);
    }

    /**
     * Removes any overrides that have been set for the given gate.
     * @param {string} gateName
     */
  }, {
    key: "clearGateOverride",
    value: function clearGateOverride(gateName) {
      Statsig.overrideGate(gateName, null);
    }

    /**
     * Adds a new override for the given config (or experiment).
     *
     * This method is additive, meaning you can call it multiple times with different experiment names to build
     * your full set of overrides.
     *
     * Overrides are persisted to the `STATSIG_JS_LITE_LOCAL_OVERRIDES` key in localStorage, so they will
     * continue to affect every client that is initialized on the same domain after this method is called.
     * If you are using this API for testing purposes, you should call ${@link FeatureGates.clearConfigOverride} after
     * your tests are completed to remove this localStorage entry.
     *
     * @param {string} experimentName
     * @param {object} values
     */
  }, {
    key: "overrideConfig",
    value: function overrideConfig(experimentName, values) {
      Statsig.overrideConfig(experimentName, values);
    }

    /**
     * Removes any overrides that have been set for the given experiment.
     * @param {string} experimentName
     */
  }, {
    key: "clearConfigOverride",
    value: function clearConfigOverride(experimentName) {
      Statsig.overrideConfig(experimentName, null);
    }

    /**
     * Set overrides for gates, experiments and layers in batch.
     *
     * Note that these overrides are **not** additive and will completely replace any that have been added
     * via prior calls to {@link FeatureGates.overrideConfig} or {@link FeatureGates.overrideGate}.
     *
     * Overrides are persisted to the `STATSIG_JS_LITE_LOCAL_OVERRIDES` key in localStorage, so they will
     * continue to affect every client that is initialized on the same domain after this method is called.
     * If you are using this API for testing purposes, you should call ${@link FeatureGates.clearAllOverrides} after
     * your tests are completed to remove this localStorage entry.
     *
     * @param {object} overrides
     */
  }, {
    key: "setOverrides",
    value: function setOverrides(overrides) {
      Statsig.setOverrides(_objectSpread({
        gates: {},
        configs: {},
        layers: {}
      }, overrides));
    }

    /**
     * @returns The current overrides for gates, configs (including experiments) and layers.
     */
  }, {
    key: "getOverrides",
    value: function getOverrides() {
      return Statsig.getOverrides();
    }

    /**
     * Clears overrides for all gates, configs (including experiments) and layers.
     */
  }, {
    key: "clearAllOverrides",
    value: function clearAllOverrides() {
      Statsig.setOverrides(null);
    }

    /**
     * Returns whether the given identifiers and customAttributes align with the current
     * set that is being used by the client.
     *
     * If this method returns false, then the {@link FeatureGates.updateUser} or {@link FeatureGates.updateUserWithValues}
     * methods can be used to re-align these values.
     *
     * @param identifiers
     * @param customAttributes
     * @returns a flag indicating whether the clients current configuration aligns with the given values
     */
  }, {
    key: "isCurrentUser",
    value: function isCurrentUser(identifiers, customAttributes) {
      return FeatureGates.shallowEquals(FeatureGates.currentIdentifiers, identifiers) && FeatureGates.shallowEquals(FeatureGates.currentAttributes, customAttributes);
    }

    /**
     * This method initializes the client using a network call to fetch the bootstrap values for the given user.
     *
     * @param clientOptions
     * @param identifiers
     * @param customAttributes
     * @private
     */
  }, {
    key: "init",
    value: (function () {
      var _init = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(clientOptions, identifiers, customAttributes) {
        var fromValuesClientOptions, experimentValues, customAttributesFromResult, clientSdkKeyPromise, experimentValuesPromise, _yield$Promise$all, _yield$Promise$all2, experimentValuesResult;
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) switch (_context5.prev = _context5.next) {
            case 0:
              fromValuesClientOptions = _objectSpread(_objectSpread({}, clientOptions), {}, {
                disableCurrentPageLogging: true
              });
              _context5.prev = 1;
              // If client sdk key fetch fails, an error would be thrown and handled instead of waiting for the experiment
              // values request to be settled, and it will fall back to use default values.
              clientSdkKeyPromise = Fetcher.fetchClientSdk(clientOptions).then(function (value) {
                return fromValuesClientOptions.sdkKey = value.clientSdkKey;
              });
              experimentValuesPromise = Fetcher.fetchExperimentValues(clientOptions, identifiers, customAttributes); // Only wait for the experiment values request to finish and try to initialise the client with experiment
              // values if both requests are successful. Else an error would be thrown and handled by the catch
              _context5.next = 6;
              return Promise.all([clientSdkKeyPromise, experimentValuesPromise]);
            case 6:
              _yield$Promise$all = _context5.sent;
              _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 2);
              experimentValuesResult = _yield$Promise$all2[1];
              experimentValues = experimentValuesResult.experimentValues;
              customAttributesFromResult = experimentValuesResult.customAttributes;
              _context5.next = 20;
              break;
            case 13:
              _context5.prev = 13;
              _context5.t0 = _context5["catch"](1);
              if (_context5.t0 instanceof Error) {
                // eslint-disable-next-line no-console
                console.error("Error occurred when trying to fetch the Feature Gates client values, error: ".concat(_context5.t0 === null || _context5.t0 === void 0 ? void 0 : _context5.t0.message));
              }
              // eslint-disable-next-line no-console
              console.warn("Initialising Statsig client without values");
              _context5.next = 19;
              return FeatureGates.initFromValues(fromValuesClientOptions, identifiers, customAttributes);
            case 19:
              throw _context5.t0;
            case 20:
              _context5.next = 22;
              return this.initFromValues(fromValuesClientOptions, identifiers, customAttributesFromResult, experimentValues);
            case 22:
            case "end":
              return _context5.stop();
          }
        }, _callee5, this, [[1, 13]]);
      }));
      function init(_x12, _x13, _x14) {
        return _init.apply(this, arguments);
      }
      return init;
    }()
    /**
     * This method initializes the client using a set of boostrap values obtained from one of the server-side SDKs.
     *
     * @param clientOptions
     * @param identifiers
     * @param customAttributes
     * @param initializeValues
     * @private
     */
    )
  }, {
    key: "initFromValues",
    value: (function () {
      var _initFromValues = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(clientOptions, identifiers, customAttributes) {
        var initializeValues,
          user,
          sdkKey,
          statsigOptions,
          _args6 = arguments;
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) switch (_context6.prev = _context6.next) {
            case 0:
              initializeValues = _args6.length > 3 && _args6[3] !== undefined ? _args6[3] : {};
              user = this.toStatsigUser(identifiers, customAttributes);
              FeatureGates.currentIdentifiers = identifiers;
              FeatureGates.currentAttributes = customAttributes;
              if (!clientOptions.sdkKey) {
                clientOptions.sdkKey = DEFAULT_CLIENT_KEY;
              }
              if (!clientOptions.eventLoggingApi) {
                clientOptions.eventLoggingApi = DEFAULT_EVENT_LOGGING_API;
              }
              if (clientOptions.perimeter === PerimeterType.FEDRAMP_MODERATE) {
                // disable all logging in FedRAMP to prevent egress of sensitive data
                clientOptions.disableAllLogging = true;
              }
              sdkKey = clientOptions.sdkKey;
              statsigOptions = this.toStatsigOptions(clientOptions, initializeValues);
              _context6.prev = 9;
              _context6.next = 12;
              return Statsig.initialize(sdkKey, user, statsigOptions);
            case 12:
              _context6.next = 21;
              break;
            case 14:
              _context6.prev = 14;
              _context6.t0 = _context6["catch"](9);
              if (_context6.t0 instanceof Error) {
                // eslint-disable-next-line no-console
                console.error("Error occurred when trying to initialise the Statsig client, error: ".concat(_context6.t0 === null || _context6.t0 === void 0 ? void 0 : _context6.t0.message));
              }
              // eslint-disable-next-line no-console
              console.warn("Initialising Statsig client with default sdk key and without values");
              _context6.next = 20;
              return Statsig.initialize(DEFAULT_CLIENT_KEY, user, _objectSpread(_objectSpread({}, statsigOptions), {}, {
                initializeValues: {}
              }));
            case 20:
              throw _context6.t0;
            case 21:
            case "end":
              return _context6.stop();
          }
        }, _callee6, this, [[9, 14]]);
      }));
      function initFromValues(_x15, _x16, _x17) {
        return _initFromValues.apply(this, arguments);
      }
      return initFromValues;
    }()
    /**
     * This method creates an instance of StatsigUser from the given set of identifiers and attributes.
     *
     * @param identifiers
     * @param customAttributes
     * @private
     */
    )
  }, {
    key: "toStatsigUser",
    value: function toStatsigUser(identifiers, customAttributes) {
      var user = {
        customIDs: identifiers,
        custom: customAttributes
      };
      if (identifiers.atlassianAccountId) {
        user.userID = identifiers.atlassianAccountId;
      }
      return user;
    }

    /**
     * This method updates the user for this client with the bootstrap values returned from a given Promise.
     * It uses the customAttributes from fetching experiment values to update the Statsig user but
     * uses the customAttributes from given input to check if the user has changed.
     *
     * @param {Identifiers} identifiers
     * @param {CustomAttributes} customAttributes
     * @param {Promise<InitializeValues>} getInitializeValues
     * @private
     */
  }, {
    key: "updateUserUsingInitializeValuesProducer",
    value: (function () {
      var _updateUserUsingInitializeValuesProducer = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7(getInitializeValues, identifiers, customAttributes) {
        var originalInitPromise, initializeValuesPromise, updateUserPromise;
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) switch (_context7.prev = _context7.next) {
            case 0:
              if (FeatureGates.initPromise) {
                _context7.next = 2;
                break;
              }
              throw new Error('The FeatureGates client must be initialized before you can update the user.');
            case 2:
              if (!FeatureGates.isCurrentUser(identifiers, customAttributes)) {
                _context7.next = 4;
                break;
              }
              return _context7.abrupt("return", FeatureGates.initPromise);
            case 4:
              // Wait for the current initialize/update to finish
              originalInitPromise = FeatureGates.initPromise;
              _context7.prev = 5;
              _context7.next = 8;
              return FeatureGates.initPromise;
            case 8:
              _context7.next = 12;
              break;
            case 10:
              _context7.prev = 10;
              _context7.t0 = _context7["catch"](5);
            case 12:
              initializeValuesPromise = getInitializeValues();
              updateUserPromise = FeatureGates.updateStatsigClientUser(initializeValuesPromise, identifiers, customAttributes); // We replace the init promise here since we are essentially re-initializing the client at this point.
              // Any subsequent calls to await FeatureGates.initialize() or FeatureGates.updateUser() will now also await this user update.
              FeatureGates.initPromise = updateUserPromise.catch(function () {
                // If the update failed then it changed nothing, so revert back to the original promise.
                FeatureGates.initPromise = originalInitPromise;
              });
              return _context7.abrupt("return", updateUserPromise);
            case 16:
            case "end":
              return _context7.stop();
          }
        }, _callee7, null, [[5, 10]]);
      }));
      function updateUserUsingInitializeValuesProducer(_x18, _x19, _x20) {
        return _updateUserUsingInitializeValuesProducer.apply(this, arguments);
      }
      return updateUserUsingInitializeValuesProducer;
    }()
    /**
     * This method updates the user on the nested Statsig client
     *
     * @param identifiers
     * @param customAttributes
     * @param initializeValuesPromise
     * @private
     */
    )
  }, {
    key: "updateStatsigClientUser",
    value: (function () {
      var _updateStatsigClientUser = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8(initializeValuesPromise, identifiers, customAttributes) {
        var initializeValues, user, _FeatureGates$initOpt2, _FeatureGates$initOpt3, errMsg, success;
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) switch (_context8.prev = _context8.next) {
            case 0:
              _context8.prev = 0;
              _context8.next = 3;
              return initializeValuesPromise;
            case 3:
              initializeValues = _context8.sent;
              user = FeatureGates.toStatsigUser(identifiers, initializeValues.customAttributesFromFetch);
              _context8.next = 12;
              break;
            case 7:
              _context8.prev = 7;
              _context8.t0 = _context8["catch"](0);
              // Make sure the updateUserCompletionCallback is called for any errors in our custom code.
              // This is not necessary for the updateUserWithValues call, because the Statsig client will already invoke the callback itself.
              errMsg = _context8.t0 instanceof Error ? _context8.t0.message : JSON.stringify(_context8.t0);
              (_FeatureGates$initOpt2 = (_FeatureGates$initOpt3 = FeatureGates.initOptions).updateUserCompletionCallback) === null || _FeatureGates$initOpt2 === void 0 || _FeatureGates$initOpt2.call(_FeatureGates$initOpt3, false, errMsg);
              throw _context8.t0;
            case 12:
              success = Statsig.updateUserWithValues(user, initializeValues.experimentValues);
              if (!success) {
                _context8.next = 18;
                break;
              }
              FeatureGates.currentIdentifiers = identifiers;
              FeatureGates.currentAttributes = customAttributes;
              _context8.next = 19;
              break;
            case 18:
              throw new Error('Failed to update user. An unexpected error occured.');
            case 19:
            case "end":
              return _context8.stop();
          }
        }, _callee8, null, [[0, 7]]);
      }));
      function updateStatsigClientUser(_x21, _x22, _x23) {
        return _updateStatsigClientUser.apply(this, arguments);
      }
      return updateStatsigClientUser;
    }()
    /**
     * This method transforms the options given by the user into the format accepted by the Statsig client.
     *
     * @param options
     * @param {Record<string, unknown>} initializeValues
     * @private
     */
    )
  }, {
    key: "toStatsigOptions",
    value: function toStatsigOptions(options, initializeValues) {
      var sdkKey = options.sdkKey,
        updateUserCompletionCallback = options.updateUserCompletionCallback,
        perimeter = options.perimeter,
        restClientOptions = _objectWithoutProperties(options, _excluded);
      return _objectSpread(_objectSpread({}, restClientOptions), {}, {
        initializeValues: initializeValues,
        environment: {
          tier: options.environment
        },
        disableCurrentPageLogging: true
      }, options.updateUserCompletionCallback && {
        updateUserCompletionCallback: FeatureGates.toStatsigUpdateUserCompletionCallback(options.updateUserCompletionCallback)
      });
    }

    /**
     * This method transforms an UpdateUserCompletionCallback in our own format into the format accepted by the Statsig client.
     *
     * @param callback
     * @private
     */
  }, {
    key: "toStatsigUpdateUserCompletionCallback",
    value: function toStatsigUpdateUserCompletionCallback(callback) {
      /**
       * The duration passed to the callback indicates how long the update took, but it is deceptive since it only times the
       * Statsig code and doesn't account for all of the extra custom work we do to obtain the bootstrap values.
       * As a result, we just suppress this parameter in our own callback rather than trying to keep it accurate.
       */
      return function (_duration, success, message) {
        callback(success, message);
      };
    }
  }, {
    key: "shallowEquals",
    value: function shallowEquals(objectA, objectB) {
      if (!objectA && !objectB) {
        return true;
      }
      if (!objectA || !objectB) {
        return false;
      }
      var aEntries = Object.entries(objectA);
      var bEntries = Object.entries(objectB);
      if (aEntries.length !== bEntries.length) {
        return false;
      }
      var ascendingKeyOrder = function ascendingKeyOrder(_ref2, _ref3) {
        var _ref4 = _slicedToArray(_ref2, 1),
          key1 = _ref4[0];
        var _ref5 = _slicedToArray(_ref3, 1),
          key2 = _ref5[0];
        return key1.localeCompare(key2);
      };
      aEntries.sort(ascendingKeyOrder);
      bEntries.sort(ascendingKeyOrder);
      for (var i = 0; i < aEntries.length; i++) {
        var _aEntries$i = _slicedToArray(aEntries[i], 2),
          aValue = _aEntries$i[1];
        var _bEntries$i = _slicedToArray(bEntries[i], 2),
          bValue = _bEntries$i[1];
        if (aValue !== bValue) {
          return false;
        }
      }
      return true;
    }

    /**
     * @returns string version of the current package in semver style.
     */
  }, {
    key: "getPackageVersion",
    value: function getPackageVersion() {
      return CLIENT_VERSION;
    }

    /**
     * Returns a specified layer otherwise returns an empty layer as a default value if the layer doesn't exist.
     *
     * @param {string} layerName - The name of the layer
     * @param {Object} options
     * @param {boolean} options.fireLayerExposure - Whether or not to fire the exposure event for the layer. Defaults to true. To log an exposure event manually at a later time, use {@link FeatureGates.manuallyLogLayerExposure} (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-)).
     * @returns A layer
     * @example
     * ```ts
     * const layer = FeatureGates.getLayer('example-layer-name');
     * const exampletitle: string = layer.get("title", "Welcome to Statsig!");
     * ```
     */
  }, {
    key: "getLayer",
    value: function getLayer(layerName) {
      var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      try {
        var _options$fireLayerExp = options.fireLayerExposure,
          fireLayerExposure = _options$fireLayerExp === void 0 ? true : _options$fireLayerExp;
        var evalMethod = fireLayerExposure ? Statsig.getLayer.bind(Statsig) : Statsig.getLayerWithExposureLoggingDisabled.bind(Statsig);
        return evalMethod(layerName);
      } catch (error) {
        // Log the first occurrence of the error
        if (!FeatureGates.hasGetLayerErrorOccurred) {
          // eslint-disable-next-line no-console
          console.warn({
            msg: 'An error has occurred getting the layer. Only the first occurrence of this error is logged.',
            layerName: layerName,
            error: error
          });
          FeatureGates.hasGetLayerErrorOccurred = true;
        }

        // Return a default value
        return Layer._create(layerName, {}, '', {
          time: Date.now(),
          reason: EvaluationReason.Error
        });
      }
    }

    /**
      * Returns the value of a given parameter in a layer config.
      *
      * @template T
      * @param {string} layerName - The name of the layer
      * @param {string} parameterName - The name of the parameter to fetch from the layer config
      * @param {T} defaultValue - The value to serve if the layer or parameter do not exist, or if the returned value does not match the expected type.
      * @param {Object} options
      * @param {boolean} options.fireLayerExposure - Whether or not to fire the exposure event for the layer. Defaults to true. To log an exposure event manually at a later time, use {@link FeatureGates.manuallyLogLayerExposure} (see [Statsig docs about manually logging exposures](https://docs.statsig.com/client/jsClientSDK#manual-exposures-))
      * @param {function} options.typeGuard - A function that asserts that the return value has the expected type. If this function returns false, then the default value will be returned instead. This can be set to protect your code from unexpected values being set remotely. By default, this will be done by asserting that the default value and value are the same primitive type.
      * @returns The value of the parameter if the layer and parameter both exist, otherwise the default value.
      * @example
      ``` ts
      type ValidColor = 'blue' | 'red' | 'yellow';
      type ValidColorTypeCheck = (value: unknown) => value is ValidColor;
       const isValidColor: ValidColorTypeCheck =
         (value: unknown) => typeof value === 'string' && ['blue', 'red', 'yellow'].includes(value);
       const buttonColor: ValidColor = FeatureGates.getLayerValue(
         'example-layer-name',
         'backgroundColor',
         'yellow',
         {
             typeGuard: isValidColor
         }
      );
      ```
     */
  }, {
    key: "getLayerValue",
    value: function getLayerValue(layerName, parameterName, defaultValue) {
      var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      var layer = FeatureGates.getLayer(layerName, options);
      try {
        var typeGuard = options.typeGuard;
        return layer.get(parameterName, defaultValue, typeGuard);
      } catch (error) {
        // Log the first occurrence of the error
        if (!FeatureGates.hasGetLayerValueErrorOccurred) {
          // eslint-disable-next-line no-console
          console.warn({
            msg: 'An error has occurred getting the layer value. Only the first occurrence of this error is logged.',
            layerName: layerName,
            defaultValue: defaultValue,
            options: options,
            error: error
          });
          FeatureGates.hasGetLayerValueErrorOccurred = true;
        }
        return defaultValue;
      }
    }
  }]);
  return FeatureGates;
}();
_defineProperty(FeatureGates, "initPromise", null);
_defineProperty(FeatureGates, "initCompleted", false);
_defineProperty(FeatureGates, "hasGetExperimentErrorOccurred", false);
_defineProperty(FeatureGates, "hasGetExperimentValueErrorOccurred", false);
_defineProperty(FeatureGates, "hasGetLayerErrorOccurred", false);
_defineProperty(FeatureGates, "hasGetLayerValueErrorOccurred", false);
_defineProperty(FeatureGates, "hasCheckGateErrorOccurred", false);
var boundFGJS = FeatureGates;

// This makes it possible to get a reference to the FeatureGates client at runtime.
// This is important for overriding values in Cyprus tests, as there needs to be a
// way to get the exact instance for a window in order to mock some of its methods.
if (typeof window !== 'undefined') {
  if (window.__FEATUREGATES_JS__ === undefined) {
    window.__FEATUREGATES_JS__ = FeatureGates;
  } else {
    var _boundFGJS, _boundFGJS$getPackage;
    boundFGJS = window.__FEATUREGATES_JS__;
    var boundVersion = ((_boundFGJS = boundFGJS) === null || _boundFGJS === void 0 || (_boundFGJS$getPackage = _boundFGJS.getPackageVersion) === null || _boundFGJS$getPackage === void 0 ? void 0 : _boundFGJS$getPackage.call(_boundFGJS)) || '4.10.0 or earlier';
    if (boundVersion !== CLIENT_VERSION) {
      var message = "Multiple versions of FeatureGateClients found on the current page.\n      The currently bound version is ".concat(boundVersion, " when module version ").concat(CLIENT_VERSION, " was loading.");
      // eslint-disable-next-line no-console
      console.warn(message);
    }
  }
}

/**
 * @property {FeatureGates} FeatureGate default export
 */
export default boundFGJS;